// src/hooks/useEntityNavigation.js
// Shared hook for entity navigation
import { useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { isUuid, formatEmailDisplayName } from '../utils';

/**
 * Custom hook that provides entity navigation logic
 * Handles clients, staff members, and departments uniformly
 * 
 * @param {Object} props - Optional props passed directly (for components outside Outlet)
 * @param {Array} props.allClients - List of all client objects
 * @param {Array} props.allDepartmentIds - List of all department IDs
 * @param {boolean} props.clientsLoaded - Whether clients have been loaded
 * @param {string} props.clientId - Current client ID (optional, will use useParams if not provided)
 * @param {string} props.staffId - Current staff ID (optional)
 * @param {string} props.departmentId - Current department ID (optional)
 */
export const useEntityNavigation = (props = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    
    // Use props if provided, otherwise fall back to URL params
    const clientId = props.clientId ?? params.clientId;
    const staffId = props.staffId ?? params.staffId;
    const departmentId = props.departmentId ?? params.departmentId;
    
    // Get data from props (passed from Dashboard)
    const { 
        allClients = [], 
        allDepartmentIds = [],
        allStaff = [],
        apiService,
        clientsLoaded = false
    } = props;

    // Determine current entity type based on URL path
    const pathSegments = location.pathname.split('/').filter(p => p);
    const mainPathSegment = pathSegments.length > 0 ? pathSegments[0] : 'accounts';

    const entityType = useMemo(() => {
        if (mainPathSegment === 'accounts' && clientId) return 'client';
        if (mainPathSegment === 'staff' && staffId) return 'staff';
        if (mainPathSegment === 'operations' && departmentId) return 'department';
        return null;
    }, [mainPathSegment, clientId, staffId, departmentId]);

    // Get current entity ID
    const currentEntityId = useMemo(() => {
        switch (entityType) {
            case 'client': return clientId;
            case 'staff': return staffId;
            case 'department': return departmentId;
            default: return null;
        }
    }, [entityType, clientId, staffId, departmentId]);

    // Get current sub-view (overview, goals, issues, etc.)
    const currentSubView = useMemo(() => {
        if (entityType === 'client' && pathSegments.length > 2) {
            return pathSegments[2];
        }
        if (entityType === 'department' && pathSegments.length > 2) {
            return pathSegments[2];
        }
        return 'overview';
    }, [entityType, pathSegments]);

    // Build list of available entities based on type
    const entities = useMemo(() => {
        switch (entityType) {
            case 'client':
                return allClients.map(client => ({
                    id: client.clientId,
                    label: getDisplayName(client.name, client.clientId),
                    raw: client
                }));
            case 'department':
                return allDepartmentIds.map(deptName => ({
                    id: deptName,
                    label: deptName,
                    raw: { name: deptName }
                }));
            case 'staff':
                // Use staff list passed from Dashboard
                return allStaff.map(staff => {
                    // Staff objects may have staffId, email, or name properties
                    const id = staff.staffId || staff.email || staff.id;
                    let label = staff.name || staff.staffName;
                    // If no name, format email nicely
                    if (!label && id && id.includes('@')) {
                        label = formatEmailDisplayName(id, id);
                    }
                    return {
                        id: id,
                        label: label || id || 'Unknown',
                        raw: staff
                    };
                });
            default:
                return [];
        }
    }, [entityType, allClients, allDepartmentIds, allStaff]);

    // Get current entity object
    const currentEntity = useMemo(() => {
        if (!currentEntityId) return null;
        const found = entities.find(e => e.id === currentEntityId);
        if (found) return found;
        
        // Entity not in list yet — build a display label that avoids raw UUIDs
        let displayLabel = currentEntityId;
        if (entityType === 'staff' && currentEntityId.includes('@')) {
            displayLabel = formatEmailDisplayName(currentEntityId, currentEntityId);
        } else if (isUuid(currentEntityId)) {
            // Never show a raw UUID; signal that data is still loading
            displayLabel = 'Loading...';
        }
        
        return {
            id: currentEntityId,
            label: displayLabel,
            raw: null
        };
    }, [entities, currentEntityId, entityType]);

    // Navigate to a different entity of the same type, preserving the current view
    const navigateToEntity = useCallback((entityId) => {
        if (!entityId) return;
        
        switch (entityType) {
            case 'client':
                navigate(`${ROUTES.ACCOUNTS}/${entityId}/${currentSubView}`);
                break;
            case 'department':
                navigate(`${ROUTES.OPERATIONS}/${entityId}/${currentSubView}`);
                break;
            case 'staff':
                navigate(`/staff/${entityId}`);
                break;
            default:
                break;
        }
    }, [entityType, currentSubView, navigate]);

    // Navigate to list view for current entity type
    const navigateToList = useCallback(() => {
        switch (entityType) {
            case 'client':
                navigate(ROUTES.ACCOUNTS);
                break;
            case 'department':
                navigate(ROUTES.OPERATIONS);
                break;
            case 'staff':
                navigate('/staff');
                break;
            default:
                break;
        }
    }, [entityType, navigate]);

    // Get entity type display labels
    const entityTypeLabels = useMemo(() => {
        switch (entityType) {
            case 'client':
                return { singular: 'Account', plural: 'Accounts', icon: 'briefcase' };
            case 'department':
                return { singular: 'Department', plural: 'Departments', icon: 'building' };
            case 'staff':
                return { singular: 'Staff Member', plural: 'Staff', icon: 'user' };
            default:
                return { singular: 'Entity', plural: 'Entities', icon: 'folder' };
        }
    }, [entityType]);

    // Filter entities by search term
    const filterEntities = useCallback((searchTerm) => {
        if (!searchTerm) return entities;
        const lowerTerm = searchTerm.toLowerCase();
        return entities.filter(entity => 
            entity.label.toLowerCase().includes(lowerTerm) ||
            entity.id.toLowerCase().includes(lowerTerm)
        );
    }, [entities]);

    return {
        // Current state
        entityType,
        currentEntityId,
        currentEntity,
        currentSubView,
        entities,
        entityTypeLabels,
        
        // Loading state
        isLoading: entityType === 'client' && !clientsLoaded,
        
        // Navigation functions
        navigateToEntity,
        navigateToList,
        filterEntities,
        
        // Context data passthrough
        apiService
    };
};

// Helper to get display name, avoiding UUID display
function getDisplayName(name, id) {
    if (name && !isUuid(name)) {
        return name;
    }
    if (id && !isUuid(id)) {
        return id;
    }
    return '(Unnamed)';
}

export default useEntityNavigation;
